import React from 'react';
import { ChevronDown, ChevronRight, Copy } from 'lucide-react';

interface ExpandableTextProps {
  title: string;
  content: string;
  isExpanded: boolean;
  onToggle: () => void;
  maxPreviewLength?: number;
  type?: 'prompt' | 'messages' | 'response';
}

const ExpandableText: React.FC<ExpandableTextProps> = ({
  title,
  content,
  isExpanded,
  onToggle,
  maxPreviewLength = 100,
  type = 'response'
}) => {
  const formatContent = (text: string): string => {
    if (type === 'messages') {
      try {
        const parsed = JSON.parse(text);
        return JSON.stringify(parsed, null, 2);
      } catch {
        return text;
      }
    }
    return text;
  };

  const formattedContent = formatContent(content);
  const previewContent = formattedContent.length > maxPreviewLength 
    ? `${formattedContent.substring(0, maxPreviewLength)}...`
    : formattedContent;

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(formattedContent);
    } catch (error) {
      console.error('Failed to copy text:', error);
    }
  };

  const getTypeColor = () => {
    switch (type) {
      case 'prompt': return '#1976D2';
      case 'messages': return '#7B1FA2';
      case 'response': return '#388E3C';
      default: return '#666';
    }
  };

  return (
    <div className="expandable-text">
      <div className="expandable-header" onClick={onToggle}>
        {isExpanded ? (
          <ChevronDown size={16} color="#666" />
        ) : (
          <ChevronRight size={16} color="#666" />
        )}
        <span className="expandable-title" style={{ color: getTypeColor() }}>
          {title}
        </span>
        <span className="expandable-length">
          ({formattedContent.length} chars)
        </span>
      </div>
      
      <div className="expandable-content">
        {isExpanded ? (
          <div className="expanded-view">
            <div className="content-header">
              <button 
                className="copy-button"
                onClick={(e) => {
                  e.stopPropagation();
                  copyToClipboard();
                }}
                title="Copy to clipboard"
              >
                <Copy size={14} />
              </button>
            </div>
            <pre className="content-text">{formattedContent}</pre>
          </div>
        ) : (
          <div className="preview-text">{previewContent}</div>
        )}
      </div>

      <style>{`
        .expandable-text {
          border: 1px solid #e0e0e0;
          border-radius: 4px;
          margin-bottom: 8px;
        }

        .expandable-header {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 8px 12px;
          cursor: pointer;
          background: #f9f9f9;
          border-bottom: 1px solid #e0e0e0;
          user-select: none;
        }

        .expandable-header:hover {
          background: #f0f0f0;
        }

        .expandable-title {
          font-weight: 500;
          font-size: 14px;
        }

        .expandable-length {
          color: #888;
          font-size: 12px;
          margin-left: auto;
        }

        .expandable-content {
          padding: 12px;
        }

        .expanded-view {
          position: relative;
        }

        .content-header {
          display: flex;
          justify-content: flex-end;
          margin-bottom: 8px;
        }

        .copy-button {
          background: #f5f5f5;
          border: 1px solid #ddd;
          border-radius: 4px;
          padding: 4px 8px;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 4px;
          font-size: 12px;
          color: #666;
        }

        .copy-button:hover {
          background: #e8e8e8;
        }

        .content-text {
          font-family: 'Courier New', monospace;
          font-size: 12px;
          line-height: 1.4;
          white-space: pre-wrap;
          word-wrap: break-word;
          background: #f8f8f8;
          padding: 12px;
          border-radius: 4px;
          margin: 0;
          max-height: 300px;
          overflow-y: auto;
          border: 1px solid #e8e8e8;
          color: #333;
        }

        .preview-text {
          font-size: 13px;
          color: #666;
          line-height: 1.4;
          white-space: pre-wrap;
          word-wrap: break-word;
        }
      `}</style>
    </div>
  );
};

export default ExpandableText;